import { useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { TagPicker } from "@/v2/components/tag-picker/TagPicker";
import { SimulatorChannel } from "@/api/simulator/simulator.model";
import {
  GetInteractionAdditionalContextResponse,
  InteractionAdditionalContextFieldsOptionsResponse,
  useInteractionAdditionalContextApi,
} from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.api";
import { InteractionAdditionalContextField } from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.model";
import { useChat } from "../simulator/context/ChatWrapper";
import { useTopicReleasePolicyApi } from "@/v2/entities/topic-release-policy/TopicReleasePolicy.api";
import { useSimulator } from "../simulator/context/SimulatorWrapper";
import { useGetTopicReleasePolicyId } from "@/v2/entities/topic-release-policy/useGetTopicReleasePolicy";
import {
  getInteractionAdditionalContextKey,
  getInteractionAdditionalContextSelector,
  useGetInteractionAdditionalContext,
  useGetInteractionAdditionalContextFieldsOptions,
} from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.hooks";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import styles from "./InteractionAdditionalContext.module.scss";

function getInteractionAdditionalContextFieldsOptionsSelector({
  customFields,
  enrichments,
  basicTicketData,
}: InteractionAdditionalContextFieldsOptionsResponse) {
  const customFieldsOptions = customFields.map(({ id, label }) => ({
    value: id,
    label,
  }));

  const enrichmentsOptions = enrichments.map(({ id, label }) => ({
    value: id,
    label,
  }));

  const basicTicketDataOptions = basicTicketData.map(({ id, label }) => ({
    value: id,
    label,
  }));

  return {
    customFieldsOptions,
    enrichmentsOptions,
    basicTicketDataOptions,
  };
}

interface InteractionAdditionalContextProps {
  releaseId: number;
  topicId: number;
  channel: SimulatorChannel;
}

function InteractionAdditionalContext({
  releaseId,
  topicId,
  channel,
}: InteractionAdditionalContextProps) {
  const queryClient = useQueryClient();

  const topicReleasePolicyApi = useTopicReleasePolicyApi();
  const interactionAdditionalContextApi = useInteractionAdditionalContextApi();

  const { data: topicReleasePolicyId, refetch: refetchTopicReleasePolicy } =
    useGetTopicReleasePolicyId(topicId, releaseId);

  const {
    data: interactionAdditionalContext,
    refetch: refetchInteractionAdditionalContext,
  } = useGetInteractionAdditionalContext({
    topicId,
    topicReleasePolicyId: topicReleasePolicyId ?? "",
    enabled: topicReleasePolicyId != null,
  });

  const {
    data: interactionAdditionalContextFieldsOptions,
    isLoading: isLoadingInteractionAdditionalContextFieldsOptions,
  } = useGetInteractionAdditionalContextFieldsOptions({
    topicId,
    topicReleasePolicyId: topicReleasePolicyId ?? "",
    enabled: topicReleasePolicyId != null,
    select: getInteractionAdditionalContextFieldsOptionsSelector,
  });

  const { mutateAsync: upsertTopicReleasePolicyAsync } = useMutation({
    mutationFn: topicReleasePolicyApi.upsertTopicReleasePolicy,
    onSuccess: () => refetchTopicReleasePolicy(),
  });

  const { mutate: upsertFields } = useMutation({
    mutationFn: interactionAdditionalContextApi.upsertFields,
    onMutate: async (vars) => {
      const { topicId, topicReleasePolicyId } = vars;
      const queryKey = getInteractionAdditionalContextKey(
        topicId,
        topicReleasePolicyId
      );

      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<GetInteractionAdditionalContextResponse>(
          queryKey
        );

      // optimistic update
      queryClient.setQueryData<GetInteractionAdditionalContextResponse>(
        queryKey,
        (_old) => {
          const newFields: InteractionAdditionalContextField[] = [];
          vars.fields.map((field) => {
            newFields.push({
              ...field,
              id: uuidv4(),
            });
          });

          return {
            fields: newFields,
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success("Fields updated successfully");
      refetchInteractionAdditionalContext();
    },
    onError: (_error, vars, context) => {
      toast.error("Failed to update fields");

      const { topicId, topicReleasePolicyId } = vars;
      const queryKey = getInteractionAdditionalContextKey(
        topicId,
        topicReleasePolicyId
      );

      // rollback
      if (context?.previousData) {
        queryClient.setQueryData<GetInteractionAdditionalContextResponse>(
          queryKey,
          context.previousData
        );
      }
    },
    onSettled: (_data, _error, vars) => {
      const { topicId, topicReleasePolicyId } = vars;
      const queryKey = getInteractionAdditionalContextKey(
        topicId,
        topicReleasePolicyId
      );

      // ensure server is the source of truth (optional if server is reliable)
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: deleteFields } = useMutation({
    mutationFn: interactionAdditionalContextApi.deleteFields,
    onSuccess: () => {
      toast.success("Fields deleted successfully");
      refetchInteractionAdditionalContext();
    },
    onError: () => void toast.error("Failed to delete fields"),
  });

  const { customFields, enrichments, basicTicketData } = useMemo(
    () => getInteractionAdditionalContextSelector(interactionAdditionalContext),
    [interactionAdditionalContext]
  );

  const { customFieldsOptions, enrichmentsOptions, basicTicketDataOptions } =
    interactionAdditionalContextFieldsOptions ?? {
      customFieldsOptions: [],
      enrichmentsOptions: [],
      basicTicketDataOptions: [],
    };

  const basicDataInAdditionalContextEnabled = useFlag(
    "basicDataInAdditionalContext",
    false
  );

  const getTopicReleasePolicyId = useCallback(async () => {
    return (
      topicReleasePolicyId ??
      (
        await upsertTopicReleasePolicyAsync({
          topicId,
          releaseId,
        })
      ).id
    );
  }, [topicReleasePolicyId, topicId, releaseId, upsertTopicReleasePolicyAsync]);

  const handleUpsertFields = useCallback(
    async (fields: Omit<InteractionAdditionalContextField, "id">[]) => {
      const topicReleasePolicyId = await getTopicReleasePolicyId();

      if (fields.length === 0) {
        return deleteFields({
          topicId,
          topicReleasePolicyId,
        });
      }

      return upsertFields({
        topicId,
        topicReleasePolicyId,
        fields,
      });
    },
    [
      topicReleasePolicyId,
      topicId,
      releaseId,
      upsertFields,
      upsertTopicReleasePolicyAsync,
    ]
  );

  const handleCustomFieldsChange = useCallback(
    (value: string[]) => {
      const newCustomFields: Omit<
        Omit<InteractionAdditionalContextField, "id">,
        "id"
      >[] = [...value.map((v) => ({ type: "customField" as const, value: v }))];

      handleUpsertFields([
        ...newCustomFields,
        ...(enrichments ?? []),
        ...(basicTicketData ?? []),
      ]);
    },
    [handleUpsertFields, enrichments, basicTicketData]
  );

  const handleEnrichmentsChange = useCallback(
    (value: string[]) => {
      const newEnrichments: Omit<InteractionAdditionalContextField, "id">[] = [
        ...value.map((v) => ({ type: "enrichment" as const, value: v })),
      ];

      handleUpsertFields([
        ...newEnrichments,
        ...(customFields ?? []),
        ...(basicTicketData ?? []),
      ]);
    },
    [handleUpsertFields, customFields, basicTicketData]
  );

  const handleBasicTicketDataChange = useCallback(
    (value: string[]) => {
      const newBasicTicketData: Omit<
        InteractionAdditionalContextField,
        "id"
      >[] = [
        ...value.map((v) => ({ type: "basicTicketData" as const, value: v })),
      ];

      handleUpsertFields([
        ...newBasicTicketData,
        ...(customFields ?? []),
        ...(enrichments ?? []),
      ]);
    },
    [handleUpsertFields, customFields, enrichments]
  );

  if (
    customFieldsOptions.length === 0 &&
    enrichmentsOptions.length === 0 &&
    basicTicketDataOptions.length === 0
  ) {
    return <></>;
  }

  return (
    <Flex
      py="5"
      px="2"
      direction="column"
      gap="4"
      data-channel={channel}
      className={styles.interactionAdditionalContext}
    >
      {customFieldsOptions.length > 0 && (
        <>
          <Flex direction="column" gap="1">
            <Text size="2" color="gray" weight="medium" highContrast>
              Context Fields
            </Text>
            <Text size="1" color="gray">
              Add fields to the interaction additional context
            </Text>
          </Flex>

          <TagPicker
            options={customFieldsOptions}
            value={customFields?.map(({ value }) => value)}
            loading={isLoadingInteractionAdditionalContextFieldsOptions}
            onChange={handleCustomFieldsChange}
            classes={{
              list: styles.contextFieldList,
              addButton: styles.addFieldButton,
            }}
          />
        </>
      )}
      {enrichmentsOptions.length > 0 && (
        <>
          <Flex direction="column" gap="1">
            <Text size="2" color="gray" weight="medium" highContrast>
              Enrichments
            </Text>
            <Text size="1" color="gray">
              Add enrichments to the interaction additional context
            </Text>
          </Flex>

          <TagPicker
            options={enrichmentsOptions}
            value={enrichments?.map(({ value }) => value)}
            loading={isLoadingInteractionAdditionalContextFieldsOptions}
            onChange={handleEnrichmentsChange}
            classes={{
              list: styles.contextFieldList,
              addButton: styles.addFieldButton,
            }}
          />
        </>
      )}

      {basicDataInAdditionalContextEnabled &&
        basicTicketDataOptions.length > 0 && (
          <>
            <Flex direction="column" gap="1">
              <Text size="2" color="gray" weight="medium" highContrast>
                Basic Ticket Data
              </Text>
              <Text size="1" color="gray">
                Add basic ticket data fields to the interaction additional
                context
              </Text>
            </Flex>

            <TagPicker
              options={basicTicketDataOptions}
              value={basicTicketData?.map(({ value }) => value)}
              loading={isLoadingInteractionAdditionalContextFieldsOptions}
              onChange={handleBasicTicketDataChange}
              classes={{
                list: styles.contextFieldList,
                addButton: styles.addFieldButton,
              }}
            />
          </>
        )}
    </Flex>
  );
}

interface InteractionAdditionalContextGuardProps {
  channel?: SimulatorChannel;
}

export function InteractionAdditionalContextGuard({
  channel,
}: InteractionAdditionalContextGuardProps) {
  const simulatorValue = useSimulator();
  const { currentRelease, isLoadingCurrentRelease, currentReleaseError } =
    simulatorValue;

  const chatValue = useChat();
  const { selectedTopic } = chatValue;

  if (currentReleaseError) {
    toast.error("Something is wrong, release is not set");
    return <Navigate to="/" />;
  }

  if (
    selectedTopic == null ||
    channel == null ||
    (channel != "autonomous_ticket_event" && channel != "autonomous_quackchat")
  ) {
    return <></>;
  }

  if (isLoadingCurrentRelease || currentRelease == null) {
    return <Spinner size="2" />;
  }

  return (
    <InteractionAdditionalContext
      releaseId={currentRelease.id}
      topicId={selectedTopic.id}
      channel={channel}
    />
  );
}
